/**
 * An attachment that is not a picture, as this client reads it.
 *
 * A twin of the phone's rules, kept deliberately in the same shape: the two clients read the same messages
 * off the same collection, and a file that is a named row on one and an unreadable image on the other is
 * the same file described two ways.
 */

/** Bytes that render as themselves. */
export function isImageMime(mime?: string): boolean {
  return mime?.startsWith("image/") === true;
}

/**
 * Whether an attachment is shown as a file rather than as a picture.
 *
 * An attachment with no mime at all is a picture: for as long as attachments existed there was nothing else
 * to send, so the only messages missing one are pictures, and reading the absence as some other kind of
 * file would turn every one of them into a row with no preview.
 */
export function rendersAsFile(mime?: string): boolean {
  return mime !== undefined && mime !== "image/*" && !isImageMime(mime);
}

/** The extension a mime implies, or empty when it implies none — used only to name a file that arrived unnamed. */
function extensionForMime(mime?: string): string {
  if (!mime) return "";
  if (mime.includes("pdf")) return "pdf";
  if (mime.includes("json")) return "json";
  if (mime.includes("zip")) return "zip";
  if (mime.includes("csv")) return "csv";
  if (mime.includes("html")) return "html";
  if (mime.startsWith("text/")) return "txt";
  return "";
}

/**
 * What to call the file.
 *
 * The sender's own name whenever there is one — it is the only description of the contents anybody chose.
 * Failing that the mime is all there is, and a bare extension at least says what would open it. Never
 * blank: a row with no name cannot be referred to, and it is a file that did arrive.
 */
export function fileLabel(name?: string, mime?: string): string {
  const given = name?.trim();
  if (given) return given;
  const ext = extensionForMime(mime);
  return ext ? `file.${ext}` : "file";
}

/** A file size as a person would say it — the unit that makes the number small. */
export function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The most an attachment may weigh.
 *
 * The bytes are base64'd in memory, encrypted, and held as one string on the way to Storage, so a large
 * file is not slow here — it is a tab that stops responding. The same ceiling as the phone's, because a
 * limit that differs by which client is open is a file that sends from one and not the other.
 */
export const MAX_ATTACHMENT_BYTES = 10_000_000;

/**
 * The byte count a base64 data URL stands for, without decoding it.
 *
 * Every four characters are three bytes, less whatever the padding stood in for — so the size a row shows
 * costs a look at the end of the string rather than a walk through the whole file.
 */
export function base64Bytes(dataUrl: string): number | null {
  const b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  if (!b64) return null;
  const padding = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.max(0, (b64.length * 3) / 4 - padding);
}

/**
 * The bytes behind a base64 data URL, at most `maxBytes` of them, or null when it holds none.
 *
 * The decode is a character at a time through the whole string, so it is the one thing here that a ten
 * megabyte file makes expensive — and the tab it happens on is the one drawing the chat. A caller that
 * only needs the head of a file says so, and pays for the head.
 *
 * The limit is applied in base64 rather than after decoding: four characters are exactly three bytes, so a
 * cut on a multiple of four is a cut on a byte boundary and the remainder is never looked at.
 */
export function decodeDataUrl(dataUrl: string, maxBytes?: number): Uint8Array | null {
  let b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  if (!b64) return null;
  if (maxBytes !== undefined) b64 = b64.slice(0, Math.ceil(maxBytes / 3) * 4);
  try {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}
